import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { ENV } from "../../utils/env.js";
import { useToaster } from '../../contexts/ToasterContext';
import { Card, CardContent } from "../../components/ui/card.jsx";
import { Button } from "../../components/ui/button.jsx";
import { Check, ChevronDown, Search, Cloud, ArrowLeft, AlertCircle } from "lucide-react";
import { toast } from "sonner";

// Provider icons and colors (duplicated from IamScanTable for independence)
const providerConfig = {
  aws: {
    name: "AWS",
    color: "#FF9900",
    icon: (
      <img
        src="/inte/AWS.png"
        alt="AWS"
        className="w-6 h-6"
      />
    )
  },
  gcp: {
    name: "GCP",
    color: "#4285F4",
    icon: (
      <img
        src="/inte/GCP.png"
        alt="GCP"
        className="w-6 h-6"
      />
    )
  },
  azure: {
    name: "Azure",
    color: "#0078D4",
    icon: (
      <img
        src="/inte/Azure.png"
        alt="Azure"
        className="w-6 h-6"
      />
    )
  }
};

const focusAreaOptions = [
  { id: "permissions", label: "Permissions" },
  { id: "roles", label: "Roles" },
  { id: "policies", label: "Policies" },
  { id: "privilege_escalation", label: "Privilege Escalation" },
  { id: "insecure_access", label: "Insecure Access" },
  { id: "access_keys", label: "Access Keys" },
  { id: "trust_relationships", label: "Trust Relationships" }
];

// Storage Account Dropdown Component (duplicated)
const StorageAccountDropdown = ({
  accounts,
  selectedAccountId,
  onSelect,
  loading,
  provider
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef(null);

  const filteredAccounts = accounts.filter(acc => {
    const matchesProvider =
      (provider === 'aws' && (acc.provider?.includes('s3') || acc.provider === 'aws')) ||
      (provider === 'azure' && acc.provider === 'azure') ||
      (provider === 'gcp' && acc.provider === 'gcp');

    const matchesSearch =
      acc.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      acc.provider?.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesProvider && matchesSearch;
  });

  const selectedAccount = accounts.find(acc => (acc.id || acc.account_id) === selectedAccountId);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchQuery("");
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const getProviderIcon = (providerName) => {
    if (providerName?.includes('s3') || providerName === 'aws') {
      return <img src="/inte/AWS.png" alt="AWS" className="w-5 h-5" />;
    } else if (providerName === 'gcp') {
      return <img src="/inte/GCP.png" alt="GCP" className="w-5 h-5" />;
    } else if (providerName === 'azure') {
      return <img src="/inte/Azure.png" alt="Azure" className="w-5 h-5" />;
    }
    return <Cloud className="w-5 h-5 text-gray-400" />;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        disabled={loading}
        className={`
          w-full px-3 py-2 border border-gray-300 rounded-lg
          focus:ring-2 focus:ring-[#44087D] focus:border-transparent
          disabled:bg-gray-50 disabled:cursor-not-allowed
          flex items-center justify-between gap-2
          ${isOpen ? 'ring-2 ring-[#44087D] border-[#44087D]' : ''}
          ${loading ? 'animate-pulse' : ''}
        `}
      >
        {loading ? (
          <span className="text-gray-400">Loading accounts...</span>
        ) : selectedAccount ? (
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className="flex-shrink-0">
              {getProviderIcon(selectedAccount.provider)}
            </div>
            <div className="flex-1 min-w-0 text-left">
              <div className="text-sm font-medium text-gray-900 truncate">
                {selectedAccount.name}
              </div>
              <div className="text-xs text-gray-500 truncate">
                {selectedAccount.provider}
              </div>
            </div>
          </div>
        ) : (
          <span className="text-gray-400">Select a storage account</span>
        )}
        <ChevronDown
          className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${isOpen ? 'transform rotate-180' : ''}`}
        />
      </button>

      {isOpen && !loading && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-hidden">
          <div className="p-2 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search accounts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#44087D] focus:border-transparent"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>

          <div className="max-h-60 overflow-y-auto">
            {filteredAccounts.length === 0 ? (
              <div className="p-4 text-center text-sm text-gray-500">
                {searchQuery ? 'No accounts found matching your search' : 'No storage accounts available'}
              </div>
            ) : (
              filteredAccounts.map((acc) => {
                const accountId = acc.id || acc.account_id;
                const isSelected = accountId === selectedAccountId;

                return (
                  <button
                    key={accountId}
                    type="button"
                    onClick={() => {
                      onSelect(accountId);
                      setIsOpen(false);
                      setSearchQuery("");
                    }}
                    className={`
                      w-full px-3 py-2.5 text-left hover:bg-gray-50 transition-colors
                      flex items-center gap-3
                      ${isSelected ? 'bg-[#F4F0FD] border-l-2 border-[#44087D]' : ''}
                    `}
                  >
                    <div className="flex-shrink-0">
                      {getProviderIcon(acc.provider)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {acc.name}
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        {acc.provider}
                      </div>
                    </div>
                    {isSelected && (
                      <Check className="w-4 h-4 text-[#44087D] flex-shrink-0" />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const IamOnboarding = ({ onBack, onSuccess }) => {
  const { showToast } = useToaster();
  const [provider, setProvider] = useState('aws');
  const [businessContext, setBusinessContext] = useState('');
  const [focusAreas, setFocusAreas] = useState([]);
  const [startHour, setStartHour] = useState(0);
  const [repeatIntervalH, setRepeatIntervalH] = useState(24);
  const [timezone, setTimezone] = useState('UTC');
  const [createLoading, setCreateLoading] = useState(false);
  
  const [storageAccounts, setStorageAccounts] = useState([]);
  const [loadingStorageAccounts, setLoadingStorageAccounts] = useState(false);
  const [selectedStorageAccountId, setSelectedStorageAccountId] = useState('');

  const fetchStorageAccounts = async () => {
    const token = localStorage.getItem("jwt");
    const baseUrl = ENV.REACT_APP_URL || "/";
    if (!token) {
      showToast("Missing auth. Please log in again.", "error");
      return;
    }
    setLoadingStorageAccounts(true);
    try {
      const res = await axios.get(`${baseUrl}api/storage/accounts`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = res.data || null;
      let list = [];
      if (Array.isArray(data)) list = data;
      else if (data && Array.isArray(data.accounts)) list = data.accounts;
      else if (data && data.items && Array.isArray(data.items))
        list = data.items;
      else if (data && data.Account) list = [data.Account];
      else list = [];

      setStorageAccounts(list);
    } catch (err) {
      console.error("Error fetching storage accounts:", err);
      const msg =
        err?.response?.data?.message || "Failed to fetch storage accounts";
      showToast(msg, "error");
    } finally {
      setLoadingStorageAccounts(false);
    }
  };

  useEffect(() => {
    fetchStorageAccounts();
    // eslint-disable-next-line
  }, []);

  const handleCreateSchedule = async () => {
    if (!businessContext.trim() || focusAreas.length === 0 || !selectedStorageAccountId) {
      showToast("Please fill in all required fields", "error");
      return;
    }

    try {
      setCreateLoading(true);
      const token = localStorage.getItem("jwt");
      const baseUrl = ENV.REACT_APP_URL || "/";

      if (!token) {
        showToast("Missing auth. Please log in again.", "error");
        return;
      }

      const now = new Date();
      now.setHours(startHour, 0, 0, 0);
      const isoStartHour = now.toISOString();
      const scheduleData = {
        provider,
        storage_account_id: selectedStorageAccountId,
        business_context: businessContext.trim(),
        focus_areas: focusAreas,
        start_hour: isoStartHour,
        repeat_interval_h: repeatIntervalH,
        timezone: timezone,
      };

      const response = await fetch(`${baseUrl}api/iam-scan-schedules`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(scheduleData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to create IAM scan schedule (status ${response.status})`);
      }

      await response.json();
      showToast("IAM scan schedule created successfully!", "success");
      if (onSuccess) onSuccess();
    } catch (err) {
      const errorMessage = err?.message || "Failed to create IAM scan schedule. Please try again.";
      showToast(errorMessage, "error");
    } finally {
      setCreateLoading(false);
    }
  };

  const handleFocusAreaChange = (areaId) => {
    setFocusAreas(prev =>
      prev.includes(areaId)
        ? prev.filter(id => id !== areaId)
        : [...prev, areaId]
    );
  };

  const handleProviderChange = (newProvider) => {
    if(newProvider === "gcp" || newProvider === "azure") {
      toast.info("Plan Upgrade Required")
      return null;
    }
    setProvider(newProvider);
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={onBack} className="rounded-full">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Create IAM Scan Schedule</h1>
          <p className="text-gray-600 text-sm">Configure a new IAM security scan for your cloud infrastructure</p>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-6">
              {/* Provider Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cloud Provider <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-3">
                  {Object.entries(providerConfig).map(([key, config]) => (
                    <label
                      key={key}
                      className={`flex-1 flex items-center justify-center gap-3 px-4 py-3 rounded-lg cursor-pointer transition-all border shadow-sm ${
                        provider === key
                          ? 'border-[#44087D] bg-[#F4F0FD] text-[#44087D] ring-1 ring-[#44087D]'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 text-gray-600'
                      }`}
                    >
                      <input
                        type="radio"
                        name="provider"
                        value={key}
                        checked={provider === key}
                        onChange={(e) => {
                          handleProviderChange(e.target.value);
                          setSelectedStorageAccountId('');
                        }}
                        className="sr-only"
                      />
                      <div className="flex items-center justify-center">
                        {config.icon}
                      </div>
                      <span className="text-sm font-semibold">{config.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Storage Account */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Storage Account <span className="text-red-500">*</span>
                </label>
                <StorageAccountDropdown
                  accounts={storageAccounts}
                  selectedAccountId={selectedStorageAccountId}
                  onSelect={setSelectedStorageAccountId}
                  loading={loadingStorageAccounts}
                  provider={provider}
                />
                {storageAccounts.length === 0 && !loadingStorageAccounts && (
                  <p className="mt-2 text-xs text-amber-600 flex items-center gap-1.5">
                    <AlertCircle className="w-3 h-3" />
                    No storage accounts available. Please add one in Settings.
                  </p>
                )}
              </div>

              {/* Business Context */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Business Context <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={businessContext}
                  onChange={(e) => setBusinessContext(e.target.value)}
                  placeholder="Describe your infrastructure policies and security concerns..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#44087D] focus:border-transparent resize-none text-sm transition-shadow"
                  rows={3}
                />
              </div>
            </div>

            <div className="flex flex-col h-full">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Focus Areas <span className="text-red-500">*</span>
              </label>
              <div className="flex-1 bg-gray-50 rounded-xl p-5 border border-gray-100">
                <div className="space-y-3.5">
                  {focusAreaOptions.map((area) => (
                    <label
                      key={area.id}
                      className="flex items-center gap-3 cursor-pointer group"
                    >
                      <div className="relative flex items-center">
                        <input
                          type="checkbox"
                          checked={focusAreas.includes(area.id)}
                          onChange={() => handleFocusAreaChange(area.id)}
                          className="peer h-4 w-4 cursor-pointer appearance-none rounded border border-gray-300 shadow-sm transition-all checked:border-[#44087D] checked:bg-[#44087D] hover:border-[#44087D]"
                        />
                        <Check className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 text-white opacity-0 peer-checked:opacity-100 transition-opacity" />
                      </div>
                      <span className={`text-sm transition-colors ${
                        focusAreas.includes(area.id) ? 'text-gray-900 font-medium' : 'text-gray-600 group-hover:text-gray-900'
                      }`}>
                        {area.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
              
              <div className="mt-6 flex justify-end gap-3">
                <Button variant="outline" onClick={onBack}>Cancel</Button>
                <Button
                  onClick={handleCreateSchedule}
                  disabled={createLoading || !businessContext.trim() || focusAreas.length === 0 || !selectedStorageAccountId}
                  className="px-6 py-2.5 bg-[#44087D] text-white hover:bg-[#5a0a9a] disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed transition-all text-sm font-medium shadow-sm hover:shadow"
                >
                  {createLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Creating Schedule...
                    </>
                  ) : (
                    'Create Schedule'
                  )}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default IamOnboarding;